import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, Send, Sparkles, Brain, ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useToast } from "@/hooks/use-toast";
import InstallAppButton from "@/components/InstallAppButton";

interface AgentMessage {
  id?: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string | null;
  tool_name?: string | null;
}

const Assistant = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [authChecked, setAuthChecked] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [memoryCount, setMemoryCount] = useState(0);
  const endRef = useRef<HTMLDivElement>(null);

  // Auth-Check + letzte Konversation laden
  useEffect(() => {
    const init = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        navigate("/auth?redirect=/assistant");
        return;
      }
      setAuthChecked(true);

      // Letzte Konversation finden
      const { data: convs } = await supabase
        .from("agent_conversations")
        .select("id")
        .order("last_message_at", { ascending: false })
        .limit(1);

      if (convs && convs.length) {
        setConversationId(convs[0].id);
        const { data: msgs } = await supabase
          .from("agent_messages")
          .select("id, role, content, tool_name")
          .eq("conversation_id", convs[0].id)
          .order("created_at", { ascending: true });
        setMessages(
          (msgs ?? []).filter(
            (m) => m.role === "user" || m.role === "assistant"
          ) as AgentMessage[]
        );
      }

      // Memory-Count
      const { count } = await supabase
        .from("agent_memory")
        .select("id", { count: "exact", head: true });
      setMemoryCount(count ?? 0);
    };
    init();
  }, [navigate]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const startNewChat = () => {
    setConversationId(null);
    setMessages([]);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        "klartech-agent",
        {
          body: { conversation_id: conversationId, message: text },
        }
      );

      if (error) throw error;
      if ((data as any).error) {
        toast({
          title: "Fehler",
          description: (data as any).error,
          variant: "destructive",
        });
        return;
      }

      setConversationId((data as any).conversation_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: (data as any).reply },
      ]);

      // Memory ggf. neu zählen
      const { count } = await supabase
        .from("agent_memory")
        .select("id", { count: "exact", head: true });
      setMemoryCount(count ?? 0);
    } catch (e: any) {
      toast({
        title: "Verbindungsfehler",
        description: e?.message ?? "Der Assistent ist gerade nicht erreichbar.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted to-background flex flex-col">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Klartech Assistent
            </h1>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Brain className="h-3 w-3" />
              {memoryCount > 0
                ? `Erinnert sich an ${memoryCount} Detail${memoryCount === 1 ? "" : "s"} über Sie`
                : "Lernt Sie kennen"}
            </p>
          </div>
          <InstallAppButton variant="outline" size="sm" label="Installieren" />
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          {messages.length === 0 && (
            <Card className="p-6 text-center">
              <Sparkles className="h-8 w-8 mx-auto text-primary mb-3" />
              <h2 className="font-semibold mb-1">
                Hallo! Wie kann ich Ihnen helfen?
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Ich kenne unsere Preise, kann Termine buchen und merke mir, was
                für Sie wichtig ist.
              </p>
              <div className="grid gap-2 text-left">
                {[
                  "Was kostet eine Senioren-Schulung?",
                  "Ich brauche einen Termin für nächste Woche",
                  "Bitte ruft mich zurück, mein Drucker geht nicht",
                ].map((q) => (
                  <button
                    key={q}
                    onClick={() => setInput(q)}
                    className="text-sm text-left px-3 py-2 rounded-lg border hover:bg-muted transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </Card>
          )}

          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-card border rounded-bl-sm"
                }`}
              >
                {m.role === "assistant" ? (
                  <div className="prose prose-sm max-w-none prose-p:my-1">
                    <ReactMarkdown>{m.content ?? ""}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-card border rounded-2xl rounded-bl-sm px-4 py-2.5">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {/* Input */}
      <div className="border-t bg-background sticky bottom-0">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex gap-2">
            {conversationId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={startNewChat}
                disabled={loading}
              >
                Neu
              </Button>
            )}
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Schreiben Sie Ihre Frage..."
              disabled={loading}
              className="flex-1"
            />
            <Button onClick={send} disabled={!input.trim() || loading}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
            KI kann Fehler machen. Wichtige Auskünfte bitte telefonisch
            bestätigen.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Assistant;
